import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _applyDecoratedDescriptor from "@babel/runtime/helpers/applyDecoratedDescriptor";
var _class;
import { parse as parseAri } from '@atlassian/cs-ari';
import { pollCloudAppProvisioningStatus, requestAppProvisioning as _requestAppProvisioning } from '@atlassian/mpac-api-client';
import { AppInstallError } from '@atlassian/mpac-spi';
import { BillingSystem, CompatibleProductId } from '@atlassian/mpac-types';
import { MarketplaceStoreInstallAppStatus } from '@atlassian/mpac-types/graphql-gateway-types';
import { log } from '@atlassian/mpac-utils';
import { arrayToMap, foldInstallableAddonIdentifier, getTokenFromUPM as _getTokenFromUPM, instanceToModule, postInstallToUPM as _postInstallToUPM, returnsPromise, throwsAppError, toInstallableAddonFromUpmAvailableApp, toInstalledAddon, unsupported, waitForPendingUPMTask as _waitForPendingUPMTask } from './connect/Util';
import { toEcosystemCloudInstallableAddon, toInstalledEcosystemCloudAddon } from './ecosystem-cloud/Util';
import getEnvConfig from './Environment';
import { fetchAllInstalledEcosystemCloudApps, getInstalledEcosystemCloudApp, getInstalledEcosystemCloudAppFromCaaS, getInstalledEcosystemCloudAppOrThrowInstallError, getRawCloudMarketplaceAppByKey, handleInstallAddonByARI } from './GraphQLGatewayApi';
import { installAddonViaStoreBFF } from './store-bff/installTasks';
import { makeAppKey } from './types/UPM.types';
import { getAllInstalledAppsFromUpm, getAvailableAppFromUpm, getInstalledAppFromUpm, getInstalledMarketplaceFromUpm, getInstalledMarketplacePluginFromUpm } from './upmApi';
const isInstallable = upmAvailableApp => upmAvailableApp.remoteInstallable || upmAvailableApp.preinstalled;
async function _getInstalledAddonInternal(addonId, environment, upmBaseUrl, gqlGatewayUrl, getInstalledAddonFlags) {
  const shouldRetryForLicenseDetails = getInstalledAddonFlags === null || getInstalledAddonFlags === void 0 ? void 0 : getInstalledAddonFlags.retryForLicenseDetails;
  const [marketplaceApp, upmInstalledApp, installedMarketplacePlugin] = await Promise.all([getRawCloudMarketplaceAppByKey(makeAppKey(addonId.key), gqlGatewayUrl), getInstalledAppFromUpm(upmBaseUrl, addonId.key), getInstalledMarketplacePluginFromUpm(upmBaseUrl, addonId.key, shouldRetryForLicenseDetails)]);
  return toInstalledAddon(marketplaceApp, upmInstalledApp, installedMarketplacePlugin, environment).fold(l => Promise.reject(l), installedAddon => installedAddon);
}
function getAllInstalledAppsFromMarketplace(upmBaseUrl, upmInstalledApps, gqlGatewayUrl) {
  return upmInstalledApps.map(upmInstalledApp => getRawCloudMarketplaceAppByKey(upmInstalledApp.key, gqlGatewayUrl).catch(error => error));
}
async function getAllInstalledAddonsInternal(environment, upmBaseUrl, gqlGatewayUrl) {
  const [upmInstalledApps, installedMarketplaceResponse] = await Promise.all([getAllInstalledAppsFromUpm(upmBaseUrl), getInstalledMarketplaceFromUpm(upmBaseUrl)]);
  const marketplaceApps = await Promise.all(getAllInstalledAppsFromMarketplace(upmBaseUrl, upmInstalledApps, gqlGatewayUrl));
  const installedMarketplacePlugins = arrayToMap(installedMarketplaceResponse.plugins, app => app.key);
  const marketplaceAppsMap = arrayToMap(marketplaceApps, app => app.appKey);
  const installedAddons = upmInstalledApps.reduce((acc, upmInstalledApp) => {
    const installedMarketplacePlugin = installedMarketplacePlugins[upmInstalledApp.key];
    const marketplaceApp = marketplaceAppsMap[upmInstalledApp.key];
    if (marketplaceApp && installedMarketplacePlugin) {
      return [...acc, ...toInstalledAddon(marketplaceApp, upmInstalledApp, installedMarketplacePlugin, environment).map(ia => [ia]).getOrElse([])];
    } else {
      console.error("Marketplace API or Installed Marketplace API didn't return a response for ".concat(upmInstalledApp.key));
      return acc;
    }
  }, []);
  return installedAddons;
}
let Addons = (_class = class Addons {
  constructor(config) {
    _defineProperty(this, "config", void 0);
    _defineProperty(this, "upmBaseUrl", void 0);
    this.config = config;
    this.upmBaseUrl = this.config.baseUrl;
  }
  async getInstalledAddons(_options) {
    const environment = getEnvConfig(this.config);
    const allInstalledAddonsInternalPromise = getAllInstalledAddonsInternal(environment, this.upmBaseUrl, this.config.gqlGatewayUrl);
    const allInstalledEcosystemCloudAppsPromise = fetchAllInstalledEcosystemCloudApps(this.config, _options.flags);
    return Promise.allSettled([allInstalledAddonsInternalPromise, allInstalledEcosystemCloudAppsPromise]).then(_ref => {
      let [allInstalledAddonsInternalResponse, allInstalledEcosystemCloudAppsResponse] = _ref;
      const allInstalledAddonsInternal = allInstalledAddonsInternalResponse.status === 'fulfilled' ? allInstalledAddonsInternalResponse.value : [];
      const allInstalledEcosystemCloudApps = allInstalledEcosystemCloudAppsResponse.status === 'fulfilled' ? allInstalledEcosystemCloudAppsResponse.value : [];
      const installedApps = [...allInstalledAddonsInternal, ...allInstalledEcosystemCloudApps];
      return {
        values: installedApps,
        totalCount: installedApps.length,
        pageSize: installedApps.length
      };
    });
  }
  uninstallAddon(_options) {
    return Promise.reject();
  }

  // eslint-disable-next-line max-lines-per-function
  async installAddon(addon, installationTargetInfo, installAddonOptions) {
    const {
      billing
    } = installationTargetInfo;
    const isAddonPaid = addon.paymentModel !== 'free';
    const billingSystemIsCCP = billing.system === BillingSystem.CCP;
    const useStoreBffForInstallation = billingSystemIsCCP && (isAddonPaid || installationTargetInfo.productName === CompatibleProductId.COMPASS);
    const provisionAppViaCCP = billingSystemIsCCP && isAddonPaid;
    const tasks = {
      requestAppProvisioning: async appKey => {
        try {
          return await _requestAppProvisioning({
            cloudId: parseAri(installationTargetInfo.contextId).cloudId,
            appKey,
            productName: installationTargetInfo.productName
          });
        } catch (error) {
          let errorJson = {};
          if (typeof error.error === 'string') {
            errorJson.error = error.error;
          } else {
            try {
              errorJson = await error.error.json();
            } catch (e) {
              log.error('install-app-parse-error', "requestAppProvisioning Failed to parse error", {
                error
              });
            }
          }
          let code = 'AppProvisioningError';
          if (errorJson.error === 'APP_REACTIVATION_NOT_SUPPORTED') {
            code = 'APP_REACTIVATION_NOT_SUPPORTED';
          }
          throw new AppInstallError({
            addon: addon.identifier,
            code,
            message: "Provisioning of app failed for ".concat(addon.identifier.toString()),
            source: error
          });
        }
      },
      getTokenFromUPM: async () => {
        try {
          return await _getTokenFromUPM(this.upmBaseUrl);
        } catch (error) {
          throw new AppInstallError({
            addon: addon.identifier,
            code: 'UPMTokenFetchError',
            message: "Fetching token from UPM failed for ".concat(addon.identifier.toString()),
            source: error
          });
        }
      },
      postInstallToUPM: async (token, key, provisioningRequestId, cmtEligible) => {
        try {
          return await _postInstallToUPM(this.upmBaseUrl, token, key, provisioningRequestId, cmtEligible);
        } catch (error) {
          let errorJson = {};
          if (typeof error.error === 'string') {
            errorJson.errorMessage = error.error;
          } else {
            try {
              errorJson = await error.error.json();
            } catch (e) {
              log.error('install-app-parse-error', "postInstallToUPM Failed to parse error", {
                error
              });
            }
          }
          throw new AppInstallError({
            addon: addon.identifier,
            code: errorJson.subCode || errorJson.contentType || 'UnknownUPMInstallSubscribeError',
            message: errorJson.errorMessage,
            source: error
          });
        }
      },
      waitForPendingUPMTask: async taskResponse => {
        try {
          return await _waitForPendingUPMTask(this.upmBaseUrl, taskResponse.links.self, 2000);
        } catch (error) {
          throw new AppInstallError({
            addon: addon.identifier,
            code: error.subCode || 'UPMInstallPostError',
            message: error.errorMessage || "Failed while polling for app installation status for ".concat(addon.identifier.toString()),
            source: error
          });
        }
      },
      getInstalledAddonInternal: async keyIdentifier => {
        try {
          const environment = getEnvConfig(this.config);
          const getInstalledAddonFlags = {
            retryForLicenseDetails: isAddonPaid
          };
          return await _getInstalledAddonInternal(keyIdentifier, environment, this.upmBaseUrl, this.config.gqlGatewayUrl, getInstalledAddonFlags);
        } catch (error) {
          throw new AppInstallError({
            addon: addon.identifier,
            code: 'FetchAddonInternalError',
            message: "Failed while constructing a app object post installation success for ".concat(addon.identifier.toString()),
            source: error
          });
        }
      },
      provisionAndInstallAddonByARIViaCCP: async (ariIdentifier, installConfig, provisioningReqId) => {
        try {
          var _cloudAppData$data, _cloudAppData$data$ec, _cloudAppData$data$ec2;
          const provisionRequestId = provisioningReqId || (await tasks.requestAppProvisioning(ariIdentifier.appKey));
          const cloudAppData = await getInstalledEcosystemCloudAppFromCaaS(ariIdentifier, installConfig);
          const appInstallationNode = (_cloudAppData$data = cloudAppData.data) === null || _cloudAppData$data === void 0 ? void 0 : (_cloudAppData$data$ec = _cloudAppData$data.ecosystem) === null || _cloudAppData$data$ec === void 0 ? void 0 : (_cloudAppData$data$ec2 = _cloudAppData$data$ec.appInstallationsByContext.nodes) === null || _cloudAppData$data$ec2 === void 0 ? void 0 : _cloudAppData$data$ec2[0];
          if (appInstallationNode) {
            var _cloudAppData$data$ex;
            await pollCloudAppProvisioningStatus(provisionRequestId);
            const extensionContext = (_cloudAppData$data$ex = cloudAppData.data.extensionContexts) === null || _cloudAppData$data$ex === void 0 ? void 0 : _cloudAppData$data$ex[0];
            const productExtensions = installConfig.product === CompatibleProductId.Jira ? extensionContext.adminPageExtensions : extensionContext.globalSettingsExtensions;
            return toInstalledEcosystemCloudAddon(installConfig.product, installConfig.baseUrl, appInstallationNode, productExtensions.filter(extension => extension.installationId === appInstallationNode.id));
          } else {
            return handleInstallAddonByARI(ariIdentifier, this.config, {
              addon: addon.identifier,
              contextId: installationTargetInfo.contextId,
              relayState: installationTargetInfo.relayState
            }, provisionRequestId);
          }
        } catch (error) {
          throw new AppInstallError({
            addon: addon.identifier,
            code: (error === null || error === void 0 ? void 0 : error.code) || 'AppProvisioningError',
            message: "Provisioning of app failed for ".concat(ariIdentifier.appKey),
            source: error
          });
        }
      }
    };
    const installAddonByKey = async keyIdentifier => {
      const {
        key: appKey
      } = keyIdentifier;
      const legacyProvisionAndInstallByKey = async function legacyProvisionAndInstallByKey() {
        let provisioningReqId = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : '';
        const provisioningRequestId = provisioningReqId || (provisionAppViaCCP ? await tasks.requestAppProvisioning(appKey) : undefined);
        const token = await tasks.getTokenFromUPM();
        const pir = await tasks.postInstallToUPM(token, appKey, provisioningRequestId, installationTargetInfo.cmtEligible);
        await tasks.waitForPendingUPMTask(pir);
      };
      if (useStoreBffForInstallation) {
        const installResult = await installAddonViaStoreBFF({
          appKey,
          installationTargetInfo,
          offeringId: addon.offeringId
        }, {
          gqlGatewayUrl: this.config.gqlGatewayUrl
        });
        if (installResult && installResult.status === MarketplaceStoreInstallAppStatus.ProvisioningSuccessInstallPending) {
          // Only for this provisioning request id, installation is allowed even in case of activation success
          await legacyProvisionAndInstallByKey('ac8d62f7-8456-416b-b309-5690b90ecb6b');
        }
      } else {
        await legacyProvisionAndInstallByKey();
      }
      return tasks.getInstalledAddonInternal(keyIdentifier);
    };
    async function installAddonByUrl(_) {
      throw new AppInstallError({
        message: 'Marketplace SPI function [InstallAddon with InstallByUrl] not implemented',
        addon: addon.identifier,
        code: 'MethodNotImplementedError'
      });
    }
    const installAddonByARI = async ariIdentifier => {
      const ecosystemInstallAddonOptions = {
        addon: addon.identifier,
        contextId: installationTargetInfo.contextId,
        relayState: installationTargetInfo.relayState,
        enableReactivateForgeAppFromEmcee: installAddonOptions === null || installAddonOptions === void 0 ? void 0 : installAddonOptions.enableReactivateForgeAppFromEmcee
      };
      const legacyProvisionAndInstallByARI = async () => {
        if (provisionAppViaCCP) {
          return tasks.provisionAndInstallAddonByARIViaCCP(ariIdentifier, this.config);
        }
        return handleInstallAddonByARI(ariIdentifier, this.config, ecosystemInstallAddonOptions);
      };
      if (useStoreBffForInstallation) {
        const installResult = await installAddonViaStoreBFF({
          appKey: ariIdentifier.appKey,
          installationTargetInfo,
          offeringId: addon.offeringId
        }, {
          gqlGatewayUrl: this.config.gqlGatewayUrl
        });
        if (installResult && installResult.status === MarketplaceStoreInstallAppStatus.ProvisioningSuccessInstallPending) {
          return tasks.provisionAndInstallAddonByARIViaCCP(ariIdentifier, this.config, 'ac8d62f7-8456-416b-b309-5690b90ecb6b');
        }
        return getInstalledEcosystemCloudAppOrThrowInstallError(ariIdentifier, this.config, ecosystemInstallAddonOptions);
      }
      return legacyProvisionAndInstallByARI();
    };
    return foldInstallableAddonIdentifier(addon.identifier)(installAddonByKey, installAddonByUrl, installAddonByARI);
  }
  upgradeAddon(_options) {
    return Promise.reject();
  }
  uninstallAddonFromSite(_options) {
    return Promise.reject();
  }
  createContainerToken(_options) {
    return Promise.reject();
  }
  getInstalledAddon(options) {
    const environment = getEnvConfig(this.config);
    const {
      gqlGatewayUrl
    } = this.config;
    return foldInstallableAddonIdentifier(options.addon)(async keyIdentifier => _getInstalledAddonInternal(keyIdentifier, environment, this.upmBaseUrl, gqlGatewayUrl, options.flags).catch(error => {
      if (error.source.status === 404) {
        return {
          status: 'NOT_FOUND',
          error
        };
      }
      throw error;
    }), _ => Promise.reject('Marketplace SPI function [getInstalledAddon with InstallByUrl] not implemented'), async ariIdentifier => {
      try {
        return await getInstalledEcosystemCloudApp(ariIdentifier, this.config, options.flags);
      } catch (_unused) {
        return {
          status: 'NOT_FOUND',
          error: {
            meta: {
              appKey: ariIdentifier.appKey
            }
          }
        };
      }
    });
  }
  getInstallableAddon(options) {
    const {
      hostCmtEnabled = false
    } = options;
    return foldInstallableAddonIdentifier(options.addon)(async keyIdentifier => {
      const upmAvailableApp = await getAvailableAppFromUpm(this.upmBaseUrl, keyIdentifier.key, hostCmtEnabled);
      return isInstallable(upmAvailableApp) ? toInstallableAddonFromUpmAvailableApp(upmAvailableApp) :
      // Pass raw upmAvailableApp in case app can't be installed, allowing consumer to decide how to handle it
      Promise.reject({
        upmAvailableApp,
        message: 'Requested add-on is not installable'
      });
    }, _ => Promise.reject('Marketplace SPI function [getInstallableAddon with InstallByUrl] not implemented'), async ariIdentifier => {
      const appKey = makeAppKey(ariIdentifier.appKey);
      const upmAvailableAppPromise = getAvailableAppFromUpm(this.upmBaseUrl, appKey, hostCmtEnabled);
      const marketplaceAppPromise = getRawCloudMarketplaceAppByKey(appKey, this.config.gqlGatewayUrl);
      const [upmAvailableApp, marketplaceApp] = await Promise.all([upmAvailableAppPromise, marketplaceAppPromise]);
      return toEcosystemCloudInstallableAddon(marketplaceApp, upmAvailableApp);
    });
  }
  rotateContainerToken(_options) {
    return Promise.reject();
  }
}, _applyDecoratedDescriptor(_class.prototype, "getInstalledAddons", [returnsPromise], Object.getOwnPropertyDescriptor(_class.prototype, "getInstalledAddons"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "uninstallAddon", [returnsPromise, unsupported], Object.getOwnPropertyDescriptor(_class.prototype, "uninstallAddon"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "installAddon", [returnsPromise, throwsAppError], Object.getOwnPropertyDescriptor(_class.prototype, "installAddon"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "upgradeAddon", [returnsPromise, unsupported], Object.getOwnPropertyDescriptor(_class.prototype, "upgradeAddon"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "uninstallAddonFromSite", [returnsPromise, unsupported], Object.getOwnPropertyDescriptor(_class.prototype, "uninstallAddonFromSite"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "createContainerToken", [returnsPromise, unsupported], Object.getOwnPropertyDescriptor(_class.prototype, "createContainerToken"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "getInstalledAddon", [returnsPromise], Object.getOwnPropertyDescriptor(_class.prototype, "getInstalledAddon"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "getInstallableAddon", [returnsPromise], Object.getOwnPropertyDescriptor(_class.prototype, "getInstallableAddon"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "rotateContainerToken", [returnsPromise, unsupported], Object.getOwnPropertyDescriptor(_class.prototype, "rotateContainerToken"), _class.prototype), _class);
export default config => instanceToModule(new Addons(config));